import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular';

/**
 * Wrapper service for Ionic ToastController providing consistent
 * feedback notifications for execution screen actions.
 *
 * All toasts display at the top of the viewport for 3 seconds
 * to avoid interfering with bottom-positioned action buttons.
 *
 * Requirement 2.3: Toast notification "✓ Sección guardada" on explicit CTA tap.
 */
@Injectable({ providedIn: 'root' })
export class ToastFeedbackService {
  private readonly toastController = inject(ToastController);

  /**
   * Shows "✓ Sección guardada" toast after explicit section save via CTA button.
   * Silent auto-saves do NOT trigger this toast.
   * Optionally accepts a section name for more specific feedback.
   */
  async showSectionSaved(sectionName?: string): Promise<void> {
    const message = sectionName
      ? `✓ ${sectionName} guardado`
      : '✓ Sección guardada';
    const toast = await this.toastController.create({
      message,
      duration: 2500,
      position: 'top',
      color: 'success',
      cssClass: 'fsm-toast-section-saved',
    });
    await toast.present();
  }

  /**
   * Shows a generic success toast with a custom message.
   */
  async showSuccess(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      position: 'top',
      color: 'success',
    });
    await toast.present();
  }

  /**
   * Shows a warning toast (e.g., "Almacenamiento lleno").
   */
  async showWarning(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      position: 'top',
      color: 'warning',
    });
    await toast.present();
  }

  /**
   * Shows ETA notification toast when the system notifies the customer.
   * Auto-dismisses after 5 seconds. GPS v2 — Requirement 18.
   */
  async showEtaNotificationSent(estimatedMinutes: number): Promise<void> {
    const toast = await this.toastController.create({
      message: `Se notificó al cliente — ETA: ${estimatedMinutes} minutos`,
      duration: 5000,
      position: 'top',
      color: 'primary',
      icon: 'location-outline',
    });
    await toast.present();
  }
}
